"use client";

import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { ProductsSearch } from "@/components/shop/ProductsSearch";
import { CartIcon } from "@/components/shop/CartIcon";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-4">
        <div className="h-9 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-20 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList>
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-foreground font-default text-sm tracking-wide hover:text-primary transition-colors relative group px-3 py-2">
                {item.label}
                <span className="absolute bottom-0 left-0 w-0 h-px bg-primary group-hover:w-full transition-all duration-500" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="space-y-2">
        <div className="h-8 bg-muted animate-pulse rounded-md" />
        <div className="h-8 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <div className="space-y-2">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="block px-4 py-2 text-foreground font-default hover:bg-accent hover:text-accent-foreground rounded-md transition-colors">
          {item.label}
        </Link>)}
    </div>;
}
function MainNavigationContent() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="header" className={`sticky top-0 z-40 w-full transition-all duration-500 ${isScrolled ? "bg-background/80 backdrop-blur-md border-b border-border/50" : "bg-background"}`}>
      <div className="container mx-auto px-4 py-6">
        <div className="flex items-center justify-between gap-8">
          {}
          <Link to="/HomePage" className="flex-shrink-0">
            <WvcLogo className="h-10 w-auto text-foreground" />
          </Link>

          {}
          <div className="hidden lg:flex items-center flex-1 justify-center">
            <MenuProvider menu_id="24">
              <HeaderMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="flex items-center gap-4">
            {}
            <div className="hidden md:block w-48">
              <ProductsSearch value={searchValue} onChange={setSearchValue} placeholder="Search blooms..." className="bg-input/20 border-b border-border rounded-md font-default text-sm" />
            </div>

            {}
            <CartIcon variant="ghost" size="icon" className="text-foreground hover:text-primary transition-colors" iconSize="size-5" />

            {}
            <Button variant="outline" size="default" className="hidden sm:flex border-primary/30 text-primary hover:bg-primary/5 rounded-md font-default uppercase tracking-widest text-xs" asChild>
              <Link to="#" className="text-foreground">
                Account
              </Link>
            </Button>

            {}
            <div className="lg:hidden">
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="text-foreground">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="bg-background border-border">
                  <div className="space-y-8 pt-8">
                    <div className="flex items-center justify-between">
                      <Link to="/HomePage">
                        <WvcLogo className="h-8 w-auto text-foreground" />
                      </Link>
                    </div>

                    {}
                    <ProductsSearch value={searchValue} onChange={setSearchValue} placeholder="Search blooms..." className="bg-input/20 border-b border-border rounded-md" />

                    {}
                    <MenuProvider menu_id="24">
                      <MobileMenuConsumer />
                    </MenuProvider>

                    {}
                    <Button variant="default" className="w-full bg-primary text-primary-foreground rounded-md font-default uppercase tracking-widest text-xs" asChild>
                      <Link to="#">Account</Link>
                    </Button>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
export default function MainNavigation() {
  return <MainNavigationContent />;
}